import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Grid,
  Dialog,
  Typography,
  Button,
  CircularProgress,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ThemePreferencesControls } from 'components/common/themePreferencesControls';
import { updateCommonTheme } from 'utils/themes/updateCommonTheme';
import { THEME_VALUES, BUTTONS, THEME_TYPES, THEME_MESSAGES } from 'utils/constants';

export const CommonThemeEditor = ({
  editableCommonTheme,
  setEditableCommonTheme,
  closeThemeList,
  refreshStoreAndBusiness,
  setRequestResponse,
  setRequestError,
}) => {
  const [actionInProgress, setActionInProgress] = useState(false);

  const [logo, setLogo] = useState('');
  const [borderRadius, setBorderRadius] = useState(THEME_VALUES.roundedRadius);
  const [primaryColor, setPrimaryColor] = useState(THEME_VALUES.defaultPrimaryColor);

  useEffect(() => {
    const setState = (editableCommonTheme) => {
      setLogo(editableCommonTheme.logoUrl);
      setBorderRadius(editableCommonTheme.borderRadius);
      setPrimaryColor(editableCommonTheme.primaryColor);
    };

    if (editableCommonTheme) {
      setState(editableCommonTheme);
    }
  }, [editableCommonTheme]);

  const closeEditor = async () => {
    setActionInProgress(false);
    setEditableCommonTheme(null);
    setBorderRadius('');
    setPrimaryColor('');
    setLogo('');
  };

  const saveChanges = async () => {
    const newTheme = {
      borderRadius,
      primaryColor,
      logoUrl: logo,
      storeId: editableCommonTheme.storeId,
    };
    try {
      setActionInProgress(true);
      await updateCommonTheme({
        initialTheme: editableCommonTheme,
        newTheme,
      });
      setRequestResponse(THEME_MESSAGES.commonThemeUpdated);
      closeThemeList();
      closeEditor();
      await refreshStoreAndBusiness();
    } catch (error) {
      setRequestError(error?.response?.data?.error || THEME_MESSAGES.somethingWrong);
      closeEditor();
    }
  };

  return (
    <>
      <Dialog
        onClose={closeEditor}
        className="common-theme-editor"
        open={!!editableCommonTheme}
        maxWidth="lg"
      >
        <DialogTitle disableTypography>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{`Edit ${THEME_TYPES.common} theme:`}</Typography>
            <IconButton aria-label="close" onClick={closeEditor}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers>
          <ThemePreferencesControls
            logo={logo}
            borderRadius={borderRadius}
            primaryColor={primaryColor}
            setPrimaryColor={setPrimaryColor}
            setBorderRadius={setBorderRadius}
            setLogo={setLogo}
            type={THEME_TYPES.common}
          />
        </DialogContent>
        <Grid classes={{ root: 'controls-container' }}>
          <Button
            color="primary"
            variant="contained"
            onClick={saveChanges}
            startIcon={actionInProgress ? <CircularProgress color="inherit" size={20} /> : null}
          >
            {BUTTONS.save}
          </Button>
          <Button color="secondary" variant="contained" onClick={closeEditor}>
            {BUTTONS.cancel}
          </Button>
        </Grid>
      </Dialog>
    </>
  );
};

CommonThemeEditor.propTypes = {
  editableCommonTheme: PropTypes.object,
  setEditableCommonTheme: PropTypes.func.isRequired,
  closeThemeList: PropTypes.func.isRequired,
  refreshStoreAndBusiness: PropTypes.func.isRequired,
  setRequestResponse: PropTypes.func.isRequired,
  setRequestError: PropTypes.func.isRequired,
};
